import React, { useState, useEffect, useCallback } from 'react';
import { emailTemplateAPI } from '../../services/api';
import { Button, Modal, FormField, Input, Textarea, ConfirmDialog } from '../../components/common';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const CATEGORIES = {
  devis:      { label: 'Devis',       icon: '📄' },
  facture:    { label: 'Factures',    icon: '🧾' },
  commande:   { label: 'Commandes',   icon: '📦' },
  client:     { label: 'Client',      icon: '👤' },
  signature:  { label: 'Signatures',  icon: '✍️' },
  extranet:   { label: 'Extranet',    icon: '🏠' },
  newsletter: { label: 'Newsletter',  icon: '📧' },
  general:    { label: 'Général',     icon: '💬' },
};

// Variables ARTIMARQ — injectées automatiquement à chaque envoi (depuis les Paramètres)
const ARTIMARQ_VARS = ['site_name','site_email','site_phone','site_address','legal_company','legal_siret','legal_rcs'];

// Variables prédéfinies par catégorie
const CATEGORY_VARIABLES = {
  devis:      ['client_name','company','quote_ref','total_ttc','total_ht','tax_amount','discount_pct','valid_until','items_summary','notes', ...ARTIMARQ_VARS],
  facture:    ['client_name','company','invoice_ref','total_ttc','total_ht','tax_amount','due_date','payment_status','items_summary', ...ARTIMARQ_VARS],
  commande:   ['client_name','company','order_ref','total_ttc','order_date','status', ...ARTIMARQ_VARS],
  client:     ['client_name','company','client_email','client_phone','address','city','zip_code','siret', ...ARTIMARQ_VARS],
  signature:  ['client_name','company','quote_ref','total_ttc','sign_url','valid_until', ...ARTIMARQ_VARS],
  extranet:   ['client_name','title','portal_url','valid_until','description', ...ARTIMARQ_VARS],
  newsletter: ['client_name','client_email','unsubscribe_url', ...ARTIMARQ_VARS],
  general:    ['client_name','company','client_email', ...ARTIMARQ_VARS],
};

export const AdminEmailPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('templates');
  const [editModal, setEditModal] = useState(false);
  const [testModal, setTestModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [catFilter,   setCatFilter]   = useState('');
  const [testEmail,   setTestEmail]   = useState('');
  const [testing,     setTesting]     = useState(false);
  const [confirmDel,       setConfirmDel]       = useState(null);
  const [confirmClearLogs, setConfirmClearLogs] = useState(false);
  const [selectedTpls,     setSelectedTpls]     = useState(new Set());
  const [bulkConfirmTpl,   setBulkConfirmTpl]   = useState(false);

  const emptyForm = { name: '', subject: '', body: '', category: 'general', variables: [], is_active: true };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([emailTemplateAPI.list(), emailTemplateAPI.logs()])
      .then(([t, l]) => { setTemplates(t.data.data || []); setLogs(l.data.data || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew  = () => { setEditing(null); setForm(emptyForm); setEditModal(true); };
  const openEdit = async (t) => {
    setEditing(t);
    setForm(emptyForm);
    setEditModal(true);
    setLoadingEdit(true);
    try {
      const { data } = await emailTemplateAPI.get(t.id);
      const full = data.data || data;
      setForm({
        ...full,
        body:      full.body      ?? '',
        subject:   full.subject   ?? '',
        name:      full.name      ?? '',
        category:  full.category  ?? 'general',
        variables: full.variables || [],
        is_active: full.is_active ?? true,
      });
    } catch {
      setForm({ ...t, body: t.body ?? '', subject: t.subject ?? '', name: t.name ?? '', category: t.category ?? 'general', variables: t.variables || [], is_active: t.is_active ?? true });
    } finally {
      setLoadingEdit(false);
    }
  };

  const save = async () => {
    if (!form.name?.trim() || !form.subject?.trim() || !form.body?.trim()) { toast.error('Nom, sujet et corps requis'); return; }
    setSaving(true);
    try {
      if (editing) await emailTemplateAPI.update(editing.id, form);
      else         await emailTemplateAPI.create(form);
      toast.success(editing ? 'Template mis à jour ✓' : 'Template créé ✓');
      setEditModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    try { await emailTemplateAPI.delete(confirmDel); toast.success('Template supprimé'); setConfirmDel(null); load(); }
    catch { toast.error('Erreur'); }
  };

  const toggleTpl    = (id) => setSelectedTpls(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isAllTplSel  = (list) => list.length > 0 && list.every(t => selectedTpls.has(t.id));
  const toggleAllTpl = (list) => setSelectedTpls(isAllTplSel(list) ? new Set() : new Set(list.map(t => t.id)));
  const bulkDelTpl   = async () => {
    let ok = 0;
    for (const id of selectedTpls) { try { await emailTemplateAPI.delete(id); ok++; } catch {} }
    toast.success(`${ok} template(s) supprimé(s)`);
    setSelectedTpls(new Set()); setBulkConfirmTpl(false); load();
  };

  const clearLogs = async () => {
    try { await emailTemplateAPI.clearLogs(); toast.success('Historique vidé'); setConfirmClearLogs(false); load(); }
    catch { toast.error('Erreur'); }
  };

  const testSmtp = async () => {
    if (!testEmail || !testEmail.includes('@')) { toast.error('Email invalide'); return; }
    setTesting(true);
    try { await emailTemplateAPI.testSmtp({ test_email: testEmail }); toast.success('Email de test envoyé ✓ — vérifiez votre boîte'); setTestModal(false); }
    catch (err) { toast.error('Échec SMTP : ' + (err.response?.data?.message || err.message)); }
    finally { setTesting(false); }
  };


  const filtered = catFilter ? templates.filter(t => t.category === catFilter) : templates;

  // Styles communs
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' };
  const badgeStyle = (active) => ({ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: active ? 'var(--success-bg)' : 'var(--bg-page)', color: active ? 'var(--success)' : 'var(--text-muted)', border: '1px solid', borderColor: active ? 'var(--success)' : 'var(--border)' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Emails & Templates</h1>
          <p className="admin-page-subtitle">{templates.length} template(s) configuré(s)</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => setTestModal(true)}>🔧 Test SMTP</Button>
          {isAdmin && <Button variant="primary" onClick={openNew}>+ Nouveau template</Button>}
        </div>
      </div>

      <div className="admin-page-content">
        {/* Onglets */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 'var(--space-md)' }}>
          {[['templates', '📋 Templates'], ['logs', '📜 Historique']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{ padding: '10px 20px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'none', borderBottom: tab === k ? '2px solid var(--brand-accent)' : '2px solid transparent', color: tab === k ? 'var(--brand-accent)' : 'var(--text-secondary)', marginBottom: -1 }}>{l}</button>
          ))}
        </div>

        {tab === 'templates' && (
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            {/* Sidebar catégories */}
            <div style={{ width: 175, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[['', '📋 Tous', templates.length], ...Object.entries(CATEGORIES).map(([k, c]) => [k, `${c.icon} ${c.label}`, templates.filter(t => t.category === k).length])].map(([k, l, n]) => (
                <button key={k} onClick={() => setCatFilter(k)} style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', textAlign: 'left', fontSize: 13, fontWeight: 600, background: catFilter === k ? 'rgba(230,57,70,.07)' : 'transparent', color: catFilter === k ? 'var(--brand-accent)' : 'var(--text-secondary)', border: 'none', cursor: 'pointer', opacity: n === 0 && k ? 0.5 : 1 }}>
                  {l} ({n})
                </button>
              ))}
            </div>

            {/* Liste */}
            <div style={{ flex: 1 }}>
              {loading ? <div className="page-loader"><div className="spinner" /></div> :
                !filtered.length ? (
                  <div style={{ textAlign: 'center', padding: 48, background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✉️</div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Aucun template</div>
                    {isAdmin && <Button variant="primary" onClick={openNew} style={{ marginTop: 12 }}>+ Créer un template</Button>}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Barre sélection multiple */}
                    {isAdmin && (
                      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none' }}>
                          <input type="checkbox" checked={isAllTplSel(filtered)} onChange={() => toggleAllTpl(filtered)} />
                          {isAllTplSel(filtered) ? 'Tout désélectionner' : `Tout sélectionner (${filtered.length})`}
                        </label>
                        {selectedTpls.size > 0 && (
                          <>
                            <span style={{ fontSize:13, fontWeight:600, color:'var(--brand-accent)' }}>{selectedTpls.size} sélectionné(s)</span>
                            <Button size="sm" variant="ghost" onClick={() => setBulkConfirmTpl(true)} style={{ color:'var(--error)' }}>🗑 Supprimer ({selectedTpls.size})</Button>
                            <button onClick={() => setSelectedTpls(new Set())} style={{ fontSize:12, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer' }}>Désélectionner</button>
                          </>
                        )}
                      </div>
                    )}
                    {filtered.map(t => (
                      <div key={t.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: `1px solid ${selectedTpls.has(t.id) ? 'var(--brand-accent)' : 'var(--border)'}` }}>
                        {isAdmin && <input type="checkbox" checked={selectedTpls.has(t.id)} onChange={() => toggleTpl(t.id)} style={{ flexShrink:0, accentColor:'var(--brand-accent)' }} />}
                        <div style={{ fontSize: 22, flexShrink: 0 }}>{CATEGORIES[t.category]?.icon || '💬'}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Sujet : {t.subject}</div>
                          {(t.variables || []).length > 0 && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                              {(t.variables || []).map(v => <span key={v} style={{ fontSize: 10, padding: '1px 6px', background: 'rgba(59,130,246,.08)', color: '#3b82f6', border: '1px solid rgba(59,130,246,.15)', borderRadius: 3, fontFamily: 'monospace' }}>{`{{${v}}}`}</span>)}
                            </div>
                          )}
                        </div>
                        <span style={badgeStyle(t.is_active)}>{t.is_active ? 'Actif' : 'Inactif'}</span>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          {isAdmin && <Button size="sm" variant="secondary" onClick={() => openEdit(t)}>✏️ Modifier</Button>}
                          {isAdmin && <Button size="sm" variant="ghost" onClick={() => setConfirmDel(t.id)}>🗑</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              }
            </div>
          </div>
        )}

        {tab === 'logs' && (
          <div style={card}>
            <div style={{ padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>Historique des envois ({logs.length})</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" variant="secondary" onClick={load}>🔄</Button>
                {isAdmin && logs.length > 0 && <Button size="sm" variant="ghost" onClick={() => setConfirmClearLogs(true)}>🗑 Vider l'historique</Button>}
              </div>
            </div>
            {!logs.length ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>Aucun envoi enregistré</div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Date</th><th>Expéditeur</th><th>Destinataire</th><th>Sujet</th><th>Statut</th></tr></thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(l.sent_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{l.sender || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}</td>
                      <td style={{ fontSize: 13 }}>{l.recipient}</td>
                      <td style={{ fontSize: 13, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.subject}</td>
                      <td><span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: l.status === 'success' ? 'var(--success-bg)' : 'var(--error-bg)', color: l.status === 'success' ? 'var(--success)' : 'var(--error)' }}>{l.status === 'success' ? '✓ Envoyé' : '✗ Erreur'}</span>{l.error_message && <div style={{ fontSize: 11, color: 'var(--error)' }}>{l.error_message}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Modal édition */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} title={editing ? `Modifier : ${editing.name}` : 'Nouveau template'} size="xl"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setEditModal(false)}>Annuler</Button><Button variant="primary" loading={saving} disabled={loadingEdit} onClick={save}>Enregistrer</Button></div>}
      >
        {loadingEdit ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : null}
        <div style={{ display: loadingEdit ? 'none' : 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-md)' }}>
            <FormField label="Nom du template" required><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Envoi devis client" /></FormField>
            <FormField label="Catégorie">
              <select className="form-input form-select" value={form.category} onChange={e => {
                const newCat = e.target.value;
                const allowed = CATEGORY_VARIABLES[newCat] || CATEGORY_VARIABLES.general;
                setForm(p => ({ ...p, category: newCat, variables: (p.variables || []).filter(v => allowed.includes(v)) }));
              }}>
                {Object.entries(CATEGORIES).map(([k, c]) => <option key={k} value={k}>{c.icon} {c.label}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Sujet de l'email" required>
            <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Ex: Votre devis {{quote_ref}} — Artimarq" />
          </FormField>
          <FormField label="Variables disponibles" hint="Cliquez pour ajouter. Les variables 🏢 ARTIMARQ sont injectées automatiquement depuis les Paramètres — pas besoin de les remplir à chaque envoi.">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {(form.variables || []).map(v => (
                <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: ARTIMARQ_VARS.includes(v) ? 'rgba(16,185,129,.08)' : 'rgba(59,130,246,.08)', border: `1px solid ${ARTIMARQ_VARS.includes(v) ? 'rgba(16,185,129,.25)' : 'rgba(59,130,246,.2)'}`, borderRadius: 4, fontSize: 12, fontFamily: 'monospace', color: ARTIMARQ_VARS.includes(v) ? '#059669' : '#3b82f6' }}>
                  {ARTIMARQ_VARS.includes(v) && <span style={{ fontSize: 10, marginRight: 2 }}>🏢</span>}
                  {`{{${v}}}`}
                  <button onClick={() => setForm(p => ({ ...p, variables: (p.variables || []).filter(x => x !== v) }))} style={{ color: '#94a3b8', cursor: 'pointer', background: 'none', border: 'none', fontSize: 14, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(CATEGORY_VARIABLES[form.category] || CATEGORY_VARIABLES.general).map(v => {
                const isSelected  = (form.variables || []).includes(v);
                const isArtimarq  = ARTIMARQ_VARS.includes(v);
                return (
                  <button key={v} onClick={() => { if (!isSelected) setForm(p => ({ ...p, variables: [...(p.variables || []), v] })); }}
                    style={{ padding: '4px 10px', fontSize: 11, fontFamily: 'monospace', borderRadius: 4, cursor: isSelected ? 'default' : 'pointer', border: '1px solid', borderColor: isSelected ? (isArtimarq ? 'rgba(16,185,129,.3)' : 'rgba(59,130,246,.3)') : 'var(--border)', background: isSelected ? (isArtimarq ? 'rgba(16,185,129,.08)' : 'rgba(59,130,246,.08)') : 'var(--bg-page)', color: isSelected ? (isArtimarq ? '#059669' : '#3b82f6') : 'var(--text-secondary)', opacity: isSelected ? 0.5 : 1 }}>
                  {isArtimarq && <span style={{ marginRight: 3, fontSize: 10 }}>🏢</span>}
                    {`{{${v}}}`}
                  </button>
                );
              })}
            </div>
          </FormField>
          <FormField label="Corps de l'email (HTML)" required>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              Variables disponibles : {(form.variables || []).map(v => <code key={v} onClick={() => setForm(p => ({ ...p, body: p.body + `{{${v}}}` }))} style={{ background: 'var(--bg-page)', padding: '1px 5px', borderRadius: 3, marginRight: 4, cursor: 'pointer', border: '1px solid var(--border)' }}>{`{{${v}}}`}</code>)}
            </div>
            <Textarea value={form.body ?? ''} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} rows={12} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="<h2>Bonjour {{client_name}},</h2><p>Votre devis {{quote_ref}}...</p>" />
          </FormField>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
            Template actif
          </label>
        </div>
      </Modal>

      {/* Modal test SMTP */}
      <Modal isOpen={testModal} onClose={() => setTestModal(false)} title="Test de connexion SMTP"
        footer={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}><Button variant="ghost" onClick={() => setTestModal(false)}>Fermer</Button><Button variant="primary" loading={testing} onClick={testSmtp}>📤 Envoyer le test</Button></div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <div style={{ padding: 14, background: 'var(--info-bg)', borderRadius: 'var(--radius-sm)', fontSize: 13 }}>
            💡 Configurez d'abord le SMTP dans <strong>Paramètres → Email</strong> (smtp.hostinger.com, port 465, SSL), puis testez ici.
          </div>
          <FormField label="Email de test" required>
            <Input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="votre@email.com" onKeyDown={e => e.key === 'Enter' && testSmtp()} />
          </FormField>
        </div>
      </Modal>

      {/* Confirmation suppression template */}
      <ConfirmDialog
        isOpen={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Supprimer le template"
        message="Ce template sera définitivement supprimé. Cette action est irréversible."
        confirmLabel="Supprimer"
        danger
      />

      {/* Confirmation suppression multiple templates */}
      <ConfirmDialog
        isOpen={bulkConfirmTpl}
        onClose={() => setBulkConfirmTpl(false)}
        onConfirm={bulkDelTpl}
        title="Supprimer la sélection"
        message={`${selectedTpls.size} template(s) seront définitivement supprimés.`}
        confirmLabel="Supprimer tout"
        danger
      />

      {/* Confirmation vider l'historique */}
      <ConfirmDialog
        isOpen={confirmClearLogs}
        onClose={() => setConfirmClearLogs(false)}
        onConfirm={clearLogs}
        title="Vider l'historique des envois"
        message="Tous les logs d'envoi seront définitivement supprimés. Cette action est irréversible."
        confirmLabel="Vider l'historique"
        danger
      />
    </div>
  );
};

export default AdminEmailPage;